import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Option from "./option";
import { useEffect, useMemo, useRef, useState } from "react";
import { assertNoDuplicateValues } from "./helpers";
import CloseRounded from "@mui/icons-material/CloseRounded";
import OptionsPopper from "./optionsPopper";
import { useError } from "../../../Context/errorContext";
import FadedScrollBox from "./fadedScrollBox";

export default function CustomAutocomplete({
  options,
  placeholder = "Type value",
  selected,
  setSelected,
  helperText,
  defaultOption,
  defaultKey,
  defaultLabel,
  defaultInputType = "Text",
  autoFocus,
}) {
  const { setAlert } = useError();
  const [open, setOpen] = useState(false);
  const [innerOpen, setInnerOpen] = useState(-1);
  const [userInput, setUserInput] = useState("");
  const innerListRef = useRef(null);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [active, setActive] = useState(defaultOption === true ? null : 0);
  const outerListRef = useRef(null);
  const [innerActive, setInnerActive] = useState(null);

  // ===== Outer =====

  useEffect(() => {
    assertNoDuplicateValues(options);
  }, [options]);

  function onCloseOuter(e) {
    //if (inputRef.current === document.activeElement && e.key !== "Escape")
    //  return;
    setInnerOpen(-1);
    if (userInput == null || userInput === "") {
      setActive(defaultOption === true ? null : 0);
    }
    setInnerActive(null);
    setOpen(false);
  }

  function addSelected(key, label, value) {
    if (!value) {
      setAlert({
        severity: "warning",
        message: "Filter value must not be empty",
      });
      return;
    }
    setSelected((prev) => [...prev, { key, label, value }]);
    setUserInput("");
    setActive(defaultOption === true ? null : 0);
    setInnerOpen(-1);
  }

  function removeSelected(i) {
    setSelected((prev) => prev.filter((s, idx) => idx !== i));
  }

  function handleInputChange(value) {
    setUserInput(value);
    setOpen(true);
  }

  // keep active item visible when moving with keys
  useEffect(() => {
    const el = outerListRef.current?.querySelector(
      `[data-idx="${active ?? 0}"]`,
    );
    const list = outerListRef.current;
    if (!el || !list) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop + el.offsetHeight)
      list.scrollTop = top - el.offsetHeight;
    else if (bottom >= list.scrollTop + list.clientHeight - el.offsetHeight)
      list.scrollTop = bottom - list.clientHeight + el.offsetHeight;
  }, [active]);

  const onOuterEnter = () => {
    const curActive = options[active];
    if (open) {
      if (active === null) {
        addSelected(defaultKey, defaultLabel, userInput);
        setOpen(false);
        return;
      }
      if (curActive.innerOptions !== undefined) {
        setInnerOpen((prev) => (prev === active ? -1 : active));
        return;
      }
      addSelected(curActive.key, curActive.label, userInput);
    } else {
      setOpen(true);
    }
  };

  // ===== Inner =====
  const outerOption = useMemo(() => {
    const outOpt = options[active];
    return outOpt;
  }, [options, active]);

  const innerOptions = useMemo(() => {
    return (
      outerOption?.innerOptions?.filter((opt) =>
        outerOption
          .getOptionLabel(opt)
          .toLowerCase()
          .includes(userInput.toLowerCase()),
      ) ?? []
    );
  }, [userInput, outerOption]);

  // arrow nav

  // keep active item visible when moving with keys
  useEffect(() => {
    const el = innerListRef.current?.querySelector(
      `[inner-data-idx="${innerActive ?? 0}"]`,
    );
    const list = innerListRef.current;
    if (!el || !list) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop + el.offsetHeight)
      list.scrollTop = top - el.offsetHeight;
    else if (bottom > list.scrollTop + list.clientHeight - el.offsetHeight)
      list.scrollTop = bottom - list.clientHeight + el.offsetHeight;
  }, [innerActive, innerListRef]);

  const onInnerEnter = () => {
    const curActive = innerOptions[innerActive];
    if (innerActive === null) {
      setInnerOpen(-1);
      return;
    }
    if (!curActive) return;
    addSelected(
      outerOption.key,
      outerOption.label,
      outerOption.getOptionValue(curActive),
    );
  };

  const onCloseInner = () => {
    setInnerOpen(-1);
    setInnerActive(null);
  };

  // ===== Combined =====
  const onKeyDownHandler = (e, set, close, onEnter, opts, isOuter) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      set((i) => {
        if (i === null) return 0;
        const idx = (i + 1) % opts.length;
        if ((!isOuter || (isOuter && defaultOption)) && idx === 0) return null;
        return idx;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      set((i) => {
        if (i === null) return opts.length - 1;
        const idx = (i - 1 + opts.length) % opts.length;
        if ((!isOuter || (isOuter && defaultOption)) && idx === opts.length - 1)
          return null;
        return idx;
      });
    } else if (e.key === "Home") {
      e.preventDefault();
      set(0);
    } else if (e.key === "End") {
      e.preventDefault();
      set(opts.length - 1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(e);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onEnter();
    } else if (e.key === "Tab") {
      onCloseOuter(e);
    }
  };

  const onKeyDown = (e) => {
    if (innerOpen === -1) {
      onKeyDownHandler(e, setActive, onCloseOuter, onOuterEnter, options, true);
    } else {
      onKeyDownHandler(
        e,
        setInnerActive,
        onCloseInner,
        onInnerEnter,
        innerOptions,
        false,
      );
    }
  };

  useEffect(() => {
    if (userInput != null && userInput !== "") {
      const opt = outerOption ?? { key: defaultKey, label: defaultLabel };
      if (opt.innerOptions) {
        setSelected((prev) => {
          const copy = [...prev];
          const temps = opt.innerOptions
            .filter((o) =>
              opt
                .getOptionLabel(o)
                .toLowerCase()
                .includes(userInput.toLowerCase()),
            )
            .map((o) => ({
              key: opt.key,
              label: opt.label,
              value: opt.getOptionValue(o).value ?? opt.getOptionValue(o),
              temp: true,
            }));
          console.log("with inner", [
            ...copy.filter((s) => s.temp !== true),
            ...(temps.length === 0
              ? [
                  {
                    key: opt.key,
                    label: opt.label,
                    value: null,
                    temp: true,
                  },
                ]
              : temps),
          ]);
          return [
            ...copy.filter((s) => s.temp !== true),
            ...(temps.length === 0
              ? [
                  {
                    key: opt.key,
                    label: opt.label,
                    value: null,
                    temp: true,
                  },
                ]
              : temps),
          ];
        });
        return;
      }
      setSelected((prev) => {
        const copy = [...prev];
        return [
          ...copy.filter((s) => s.temp !== true),
          {
            key: opt.key,
            label: opt.label,
            value: userInput,
            temp: true,
          },
        ];
      });
    } else {
      if (selected.find((s) => s.temp === true)) {
        setSelected((prev) => prev.filter((s) => s.temp !== true));
      }
    }
  }, [userInput, defaultKey, defaultLabel, outerOption]);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 300,
        pb: 1,
      }}
    >
      <Box
        sx={(t) => ({
          borderRadius: 1,
          border: `1px solid ${t.palette.divider}`,
          transition: "background-color .15s, border-color .15s",
          "&:hover": {
            borderColor: t.palette.text.primary, // stronger border on hover
          },
          "&:hover .MuiDivider-root": {
            backgroundColor: t.palette.text.primary,
          },
        })}
      >
        {selected.filter((s) => s.temp !== true).length !== 0 && (
          <Box onClick={() => queueMicrotask(() => inputRef.current.focus())}>
            <Stack
              direction="row"
              flexWrap="wrap"
              sx={{
                m: 0.5,
              }}
            >
              {selected
                .filter((s) => s.temp !== true)
                .map((s, i) => {
                  return (
                    <Chip
                      size="small"
                      key={i}
                      label={`${s.label} = ${s.value.label ?? s.value}`}
                      deleteIcon={<CloseRounded />}
                      onDelete={(e) => {
                        e.stopPropagation(); // don’t trigger onClick of the chip
                        removeSelected(i);
                        queueMicrotask(() => inputRef.current.focus());
                      }}
                      sx={{
                        m: 0.5,
                      }}
                    ></Chip>
                  );
                })}
            </Stack>
            <Divider
              sx={(t) => ({
                transition: "background-color .15s ease",
                backgroundColor: t.palette.divider,
              })}
            />
          </Box>
        )}
        <TextField
          autoFocus={autoFocus}
          variant="outlined"
          type={outerOption?.inputType ?? defaultInputType}
          value={userInput}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="new-password"
          onClick={() => {
            if (open) onCloseOuter();
            else setOpen((prev) => !prev);
          }}
          inputRef={inputRef}
          onKeyDown={onKeyDown}
          ref={anchorRef}
          InputProps={{
            startAdornment: userInput != null && userInput !== "" && (
              <InputAdornment
                position="start"
                sx={{
                  width: "fit-content",
                  maxWidth: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "visible",
                  }}
                >
                  {outerOption?.label ?? defaultLabel} =
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { border: "none" }, // default
              "&:hover fieldset": { border: "none" }, // hover
              "&.Mui-focused fieldset": { border: "none" }, // focus
            },
          }}
        ></TextField>
      </Box>
      <Typography
        sx={(t) => ({
          // base look
          ...t.typography.caption, // small type
          color: t.palette.text.secondary,
          lineHeight: 1.25,
          marginTop: t.spacing(0.5),

          // layout (icon + text, etc.)
          display: "flex",
          alignItems: "center",
          gap: t.spacing(0.5),

          // optional state selectors when used under a FormControl
          ".MuiFormControl-root.Mui-disabled &": {
            color: t.palette.text.disabled,
          },
          ".MuiFormControl-root.Mui-focused &": {
            color: t.palette.primary.main,
          },
          "&.Mui-error": { color: t.palette.error.main },
        })}
      >
        {helperText}
      </Typography>
      <OptionsPopper open={open} onClose={onCloseOuter} anchorRef={anchorRef}>
        <FadedScrollBox listRef={outerListRef} onKeyDown={onKeyDown}>
          {options.map((option, i) => (
            <Option
              key={i}
              index={i}
              active={i === active}
              dataIdx={i}
              innerListRef={innerListRef}
              outerListRef={outerListRef}
              value={userInput}
              option={option}
              onSelect={addSelected}
              innerOpen={innerOpen}
              setInnerOpen={setInnerOpen}
              innerActive={innerActive}
              setInnerActive={setInnerActive}
              innerOptions={innerOptions}
              setActive={setActive}
              inputFieldRef={inputRef}
            ></Option>
          ))}
        </FadedScrollBox>
      </OptionsPopper>
    </Box>
  );
}
